// Copyright 2023 Amazon.com, Inc. or its affiliates. All Rights Reserved.

import React, { useState, useMemo } from "react";
import { AuthConfigurationStatus, AuthStatus } from "../../../../enums";
import { ICredentials } from "../../../../types";
import { Trans, useTranslation } from "react-i18next";
import { AuthState, AuthStrategy } from "../../../../types";
import { EUCErrorAlert } from "../../../general/ErrorAlert/EUCErrorAlert";
import { ERROR_STATE } from "../../../../constants";
import WebGLCanvas from "../WebGLCanvas";
import { BrandedHorizontalSplitLayout, useBreakpoint } from "@amzn/aws-euc-ui";
import EUCFooter from "../../../EUCSignInComponents/Footer";
import EUCHeader from "../../../EUCSignInComponents/Header";
import BrandedFooter from "../../../EUCSignInComponents/BrandedFooter";
import { Header } from "@cloudscape-design/components";
import useDisplayWebcamWarningFlag from "../../../../hooks/useDisplayWebcamWarningFlag";
import "./style.css";
import { getRebrandedTranslation } from "../../../../i18n";
import { useGetBrandLogo } from "../../../../hooks/useGetBrandLogo";
import { CustomColorThemeSpan } from "../../../general/CustomColorTheme/CustomColorTheme";
import {
    WelcomeTextLengthClassName,
    WelcomeTextLengthThreshold,
} from "../../../../constants/Styles";

interface Props {
    onSignIn: (authState: AuthState) => void;
    authStrategy: AuthStrategy;
    errorState?: Record<string, any>;
}

const EUCSignInPage = (props: Props): JSX.Element => {
    const { onSignIn, authStrategy, errorState } = props;
    const { t } = useTranslation();

    const [authConfigurationStatus, setAuthConfigurationStatus] = useState(
        AuthConfigurationStatus.LOADING
    );
    const [authStatus, setAuthStatus] = useState(AuthStatus.LOADING);
    const [chromeCompatible, setChromeCompatible] = useState(true);
    const [renderAuthError, setRenderAuthError] = useState(
        errorState !== undefined
    );

    const [
        displayWebcamWarningFlag,
        updateDisplayWebcamWarningFlag,
    ] = useDisplayWebcamWarningFlag();

    const { isDesktopOrLaptop } = useBreakpoint();

    const brandedDesktopLogo = useGetBrandLogo("desktop");
    const brandedMobileLogo = useGetBrandLogo("mobileTable");

    const onAuthConfigured = () => {
        setAuthConfigurationStatus(AuthConfigurationStatus.CONFIGURED);
    };

    const onAuthCheck = (
        currentAuthStatus: AuthStatus,
        credentials?: ICredentials
    ) => {
        setAuthStatus(currentAuthStatus);
        if (currentAuthStatus === AuthStatus.SIGNED_IN) {
            onSignIn({
                authStatus: currentAuthStatus,
                credentials,
            });
        }
    };

    const welcomeText = t("euc.signIn.product.description");
    const welcomeTextLengthStyle = useMemo(() => {
        // Since Latin characters and CJK characters have different visual widths,
        // we use byte length as an approximation (Latin chars ≈ 1 byte, CJK chars ≈ 3 bytes in UTF-8)
        // to estimate the display width of text
        const byteSize = new TextEncoder().encode(welcomeText).length;
        if (byteSize > WelcomeTextLengthThreshold.EXTRA_EXTRA_LONG) {
            return WelcomeTextLengthClassName.EXTRA_EXTRA_LONG;
        } else if (byteSize > WelcomeTextLengthThreshold.EXTRA_LONG) {
            return WelcomeTextLengthClassName.EXTRA_LONG;
        } else if (byteSize > WelcomeTextLengthThreshold.LONG) {
            return WelcomeTextLengthClassName.LONG;
        } else if (byteSize > WelcomeTextLengthThreshold.MEDIUM) {
            return WelcomeTextLengthClassName.MEDIUM;
        } else if (byteSize > WelcomeTextLengthThreshold.SHORT) {
            return WelcomeTextLengthClassName.SHORT;
        } else {
            return WelcomeTextLengthClassName.EXTRA_SHORT;
        }
    }, [welcomeText]);

    if (authConfigurationStatus === AuthConfigurationStatus.LOADING) {
        return authStrategy.configure(onAuthConfigured);
    } else if (authStatus === AuthStatus.LOADING) {
        return authStrategy.checkAuth(onAuthCheck);
    }

    return (
        <div className="euc-container">
            <BrandedHorizontalSplitLayout
                brandLogoSrc={isDesktopOrLaptop && brandedDesktopLogo}
                backgroundImageSrc={""} // Use body background-image instead
                brandLogoAlt={getRebrandedTranslation("euc.signIn.logo.alt", t)}
                brandDescription={
                    <CustomColorThemeSpan
                        additionalClassNames={[
                            "welcome-text",
                            welcomeTextLengthStyle,
                        ]}
                    >
                        {welcomeText}
                    </CustomColorThemeSpan>
                }
                footerComponent={<EUCFooter />}
                headerComponent={<EUCHeader />}
                brandFooterComponent={<BrandedFooter />}
            >
                <div className="euc-main-container-style">
                    <div
                        className={
                            isDesktopOrLaptop
                                ? "euc-sign-in-parent-container-style"
                                : "euc-sign-in-parent-container-mobile-style"
                        }
                    >
                        {/* Banner for Webcam support on non-chromium browsers */}
                        <EUCErrorAlert
                            type={ERROR_STATE.WARNING}
                            message={t(
                                "euc.signIn.warningAlert.webcamIncompatibleWarn.message"
                            )}
                            onDismissErrorAlert={() => {
                                updateDisplayWebcamWarningFlag(true);
                            }}
                            visible={
                                !authStrategy.incomplete() &&
                                displayWebcamWarningFlag
                            }
                        />

                        <EUCErrorAlert
                            type={ERROR_STATE.ERROR}
                            header={t("authentication.errorAlert.error.header")}
                            message={
                                errorState !== undefined
                                    ? errorState.errorMessage
                                    : ""
                            }
                            dismissible={true}
                            onDismissErrorAlert={() => {
                                setRenderAuthError(false);
                            }}
                            visible={renderAuthError}
                        />

                        <EUCErrorAlert
                            type={ERROR_STATE.ERROR}
                            message={t(
                                "euc.signIn.errorAlert.chromeCompatibleError.message"
                            )}
                            dismissible={false}
                            onDismissErrorAlert={() => {}}
                            visible={
                                !authStrategy.incomplete() && !chromeCompatible
                            }
                        />

                        <WebGLCanvas
                            onChromeNotCompatible={() =>
                                setChromeCompatible(false)
                            }
                        />
                        <div className="euc-sign-in-container-style">
                            <div className="euc-sign-in-container-child-style">
                                {!isDesktopOrLaptop && brandedMobileLogo && (
                                    <img
                                        src={brandedMobileLogo}
                                        className="euc-sign-in-container-mobile-brand-logo-style"
                                    />
                                )}
                                {authStrategy.incomplete() ? (
                                    <div className="euc-incomplete-header">
                                        <Header
                                            description={t(
                                                "euc.signIn.portalStatus.alert.incomplete.message"
                                            )}
                                        >
                                            {t(
                                                "euc.signIn.portalStatus.alert.incomplete.header"
                                            )}
                                        </Header>
                                    </div>
                                ) : (
                                    <>
                                        <div className="euc-sign-in-header">
                                            <Header
                                                description={
                                                    <CustomColorThemeSpan>
                                                        <Trans i18nKey="euc.signIn.header.description" />
                                                    </CustomColorThemeSpan>
                                                }
                                            >
                                                <CustomColorThemeSpan>
                                                    <Trans i18nKey="euc.signIn.header.label" />
                                                </CustomColorThemeSpan>
                                            </Header>
                                        </div>
                                        <div className="euc-btn-container-style">
                                            {authStrategy.authButton()}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </BrandedHorizontalSplitLayout>
        </div>
    );
};

export default EUCSignInPage;
